if (!customElements.get('ws-related-carousel')) {
  customElements.define('ws-related-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      queueMicrotask(() => {
        if (this.isConnected && !this.track) this.initialize();
      });
    }

    initialize() {
      this.track = this.querySelector('.ws-related-carousel__track');
      this.items = Array.from(this.querySelectorAll('.ws-related-carousel__item'));
      this.controls = this.querySelector('.ws-related-carousel__controls');
      this.previousButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.currentLabel = this.querySelector('[data-carousel-current]');
      this.totalLabel = this.querySelector('[data-carousel-total]');
      if (!this.track || !this.items.length || !this.controls || !this.previousButton || !this.nextButton) return;

      this.currentPage = 0;
      const options = { signal: this.controller.signal };
      this.previousButton.addEventListener('click', () => this.goTo((this.destination ?? this.currentPage) - 1), options);
      this.nextButton.addEventListener('click', () => this.goTo((this.destination ?? this.currentPage) + 1), options);
      this.track.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
      for (const event of ['pointerdown', 'touchstart', 'wheel']) {
        this.track.addEventListener(event, () => { this.destination = null; }, { ...options, passive: true });
      }
      this.track.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      this.track.addEventListener('focusin', (event) => {
        const index = this.items.indexOf(event.target.closest('.ws-related-carousel__item'));
        if (index >= 0) this.goTo(Math.floor(index / this.perPage), false);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.track);
      this.layout();
    }

    layout() {
      const style = getComputedStyle(this.track);
      const perPage = Math.max(1, parseInt(style.getPropertyValue('--ws-related-columns'), 10) || 2);
      const width = this.track.clientWidth;
      const gap = parseFloat(style.columnGap) || 0;
      if (!width || (width === this.width && perPage === this.perPage && gap === this.gap)) return;
      const firstIndex = this.currentPage * (this.perPage || perPage);
      this.width = width;
      this.gap = gap;
      this.perPage = perPage;
      this.rtl = style.direction === 'rtl';
      this.totalPages = Math.ceil(this.items.length / perPage);
      this.pageSize = width + gap;
      const emptySlots = (perPage - this.items.length % perPage) % perPage;
      this.track.style.setProperty('--ws-related-empty-slots', emptySlots);
      this.track.classList.toggle('has-empty-slots', this.totalPages > 1 && emptySlots > 0);
      this.items.forEach((item, index) => {
        item.style.scrollSnapAlign = index % perPage === 0 ? 'start' : 'none';
      });
      this.controls.hidden = this.totalPages <= 1;
      this.totalLabel.textContent = this.totalPages;
      this.goTo(Math.floor(firstIndex / perPage), false);
    }

    goTo(page, animate = true) {
      if (!this.totalPages) return;
      this.destination = Math.max(0, Math.min(page, this.totalPages - 1));
      this.currentPage = this.destination;
      this.updateControls();
      this.track.scrollTo({
        left: this.destination * this.pageSize * (this.rtl ? -1 : 1),
        behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant',
      });
      if (!animate) this.destination = null;
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.currentPage = Math.max(0, Math.min(Math.round(Math.abs(this.track.scrollLeft) / this.pageSize), this.totalPages - 1));
        this.updateControls();
      });
      this.scrollTimer = setTimeout(() => { this.destination = null; }, 150);
    }

    updateControls() {
      this.currentLabel.textContent = this.currentPage + 1;
      this.previousButton.disabled = this.currentPage === 0;
      this.nextButton.disabled = this.currentPage >= this.totalPages - 1;
    }

    onKeyDown(event) {
      if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
      const current = this.destination ?? this.currentPage;
      let page;
      if (event.key === 'Home') page = 0;
      else if (event.key === 'End') page = this.totalPages - 1;
      else if (event.key === 'ArrowRight') page = current + (this.rtl ? -1 : 1);
      else if (event.key === 'ArrowLeft') page = current + (this.rtl ? 1 : -1);
      else return;
      event.preventDefault();
      this.goTo(page);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.scrollTimer);
      this.track = null;
      this.width = null;
      this.destination = null;
    }
  });
}
